import { HousingUnitFilters } from '@reliance/types/filters/HousingUnitFilters';
import { Facility } from '@reliance/types/models/Facility';
import { HousingUnit } from '@reliance/types/models/HousingUnit';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Preview from '../facility/Preview';
import { useHousingUnits } from '../hooks/HousingUnit';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import BaseHtmlTable from '../table/BaseHtmlTable';
import facilityIds from '../table/filters/facilityIds';
import { useServerTableFilterState } from '../table/useServerTableFilterState';
import AddEditModal from './AddEditModal';

const DEFAULT_ORDER_BY = 'facility.name,status,name';

export type HousingUnitListProps = {
  facility?: Facility;
  /** When set (e.g. on facility detail), reports list loading to that tab title. */
  notifyTabLoadingId?: string;
};

const HousingUnitList: React.FC<HousingUnitListProps> = (props) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editUnit, setEditUnit] = useState<HousingUnit | null>(null);

  const scopedFacilityId = props.facility?.id;

  const queryColumnDefs = useMemo(
    () => [
      { id: 'name', meta: { sortName: 'name' } },
      ...(scopedFacilityId == null
        ? [{ id: 'facilityId', meta: { filterComponent: 'FacilityIdsFilter', sortName: 'facility.name' } }]
        : []),
      { id: 'status', meta: { sortName: 'status' } },
      { id: 'emailAddress', meta: { sortName: 'emailAddress' } },
      { id: 'mobileDeviceGroup', meta: { sortName: 'mobileDeviceGroup.name' } },
      { id: 'options' },
    ],
    [scopedFacilityId]
  );

  const baseRecord = useMemo(
    () => ({ orderBy: DEFAULT_ORDER_BY }) as Record<string, unknown>,
    []
  );
  const tableState = useServerTableFilterState(queryColumnDefs, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: 100 },
  });

  const filters = useMemo((): HousingUnitFilters => {
    const m = tableState.mergedRecord;
    const facilityIdRaw = m.facilityId as number | number[] | undefined;
    const fromTable =
      facilityIdRaw == null ? undefined : Array.isArray(facilityIdRaw) ? facilityIdRaw[0] : facilityIdRaw;
    const facilityId =
      scopedFacilityId != null
        ? String(scopedFacilityId)
        : fromTable != null
          ? String(fromTable)
          : undefined;
    const name = m.name as string | undefined;
    const status = m.status as string | undefined;
    return {
      page: m.page as number,
      perPage: m.perPage as number,
      orderBy: (m.orderBy as string | undefined) ?? DEFAULT_ORDER_BY,
      ...(facilityId != null && { facilityId }),
      ...(name != null && name !== '' && { name }),
      ...(status != null && status !== '' && { status }),
    };
  }, [tableState.mergedRecord, scopedFacilityId]);

  const housingUnits = useHousingUnits(filters);

  useNotifyTabLoading(props.notifyTabLoadingId ?? null, housingUnits.loading);

  const rows = housingUnits.result?.data ?? [];
  const total = housingUnits.result?.total ?? 0;
  const perPage = housingUnits.result?.perPage ?? tableState.pagination.pageSize;
  const totalPages = Math.ceil(total / perPage) || 1;
  const page = housingUnits.result?.page ?? tableState.pagination.pageIndex + 1;

  const columnHelper = createColumnHelper<HousingUnit>();

  const columnDefs = useMemo(() => {
    const cols = [
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Name',
        meta: { sortName: 'name' },
        cell: (info) => {
          const u = info.row.original;
          return (
            <Link to={`/housing-units/view/${u.id}`}>{info.getValue()}</Link>
          );
        },
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: 'Status',
        meta: { sortName: 'status' },
        cell: (info) => {
          const active = info.getValue() === 'Active';
          return (
            <span className={active ? 'txt-color-greenDark' : 'txt-color-red'}>
              {info.getValue()}
            </span>
          );
        },
      }),
      columnHelper.accessor('emailAddress', {
        id: 'emailAddress',
        header: 'Email Address',
        meta: { sortName: 'emailAddress' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.mobileDeviceGroup?.name, {
        id: 'mobileDeviceGroup',
        header: 'Device Group',
        meta: { sortName: 'mobileDeviceGroup.name' },
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'options',
        header: '',
        enableColumnFilter: false,
        cell: (info) => (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setEditUnit(info.row.original);
              setShowAddModal(true);
            }}>
            Edit
          </Button>
        ),
      }),
    ];

    if (scopedFacilityId == null) {
      cols.splice(
        1, 0,
        columnHelper.accessor((row) => row.facility?.name ?? '', {
          id: 'facilityId',
          header: 'Facility',
          filterFn: facilityIds,
          meta: {
            filterComponent: 'FacilityIdsFilter',
            sortName: 'facility.name',
          },
          cell: (info) => {
            const f = info.row.original.facility;
            return f ? <Preview facility={f} /> : null;
          },
        })
      );
    }

    return cols;
  }, [columnHelper, scopedFacilityId]);

  return (
    <>
      <div className="mb-3">
        <Button
          variant="primary"
          onClick={() => {
            setEditUnit(null);
            setShowAddModal(true);
          }}>
          Add Housing Unit
        </Button>
      </div>

      <BaseHtmlTable
        rows={rows}
        columnDefs={columnDefs}
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        serverPagination={tableState.serverPagination}
        onServerPaginationChange={tableState.onServerPaginationChange}
        serverSorting={tableState.serverSorting}
        onServerSortingChange={tableState.onServerSortingChange}
        serverColumnFilters={tableState.serverColumnFilters}
        onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
        paginationMode="server"
        loading={housingUnits.loading}
        pageSizeOptions={[100, 200, 500]}
      />

      {showAddModal && (
        <AddEditModal
          facility={props.facility}
          housingUnit={editUnit ?? undefined}
          onClose={(saved) => {
            if (saved) {
              void housingUnits.execute();
            }
            setShowAddModal(false);
            setEditUnit(null);
          }}
        />
      )}
    </>
  );
};

export default HousingUnitList;
